import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext.js';
import { WORKING_IN_STORE_TOAST_KEY } from '../../Utils/storePickSession.js';
import {
    BILLING_TOAST_STORAGE_KEY,
    PAYMENT_REQUIRED_EVENT,
    consumePendingBillingToast,
} from '../../Utils/billingPaymentRequired.js';
import Toast from './Toast.jsx';
import useToast from '../../Utils/useToast.js';

/**
 * Shows a one-shot toast from `location.state.flashToast`, then clears that key
 * so the message survives a redirect (e.g. Manage Store → Select Store → Home).
 * Also handles billing 402 toasts and navigates to Billing / Select Plan.
 */
export default function FlashToastHost() {
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { toast, showToast, hideToast } = useToast();

    useEffect(() => {
        try {
            const storeName = sessionStorage.getItem(WORKING_IN_STORE_TOAST_KEY);
            if (storeName) {
                sessionStorage.removeItem(WORKING_IN_STORE_TOAST_KEY);
                showToast(t('selectStore.workingIn', { name: storeName }), 'info');
            }
        } catch {
            /* ignore */
        }
    }, [showToast, t]);

    useEffect(() => {
        const pending = consumePendingBillingToast();
        if (pending?.message) {
            showToast(pending.message, pending.type || 'warning');
        }
    }, [location.pathname, location.search, showToast]);

    useEffect(() => {
        const onPaymentRequired = (event) => {
            const detail = event.detail || {};
            if (detail.navigate && detail.target) {
                navigate(detail.target);
                return;
            }
            if (detail.message) {
                try {
                    sessionStorage.removeItem(BILLING_TOAST_STORAGE_KEY);
                } catch {
                    /* ignore */
                }
                showToast(detail.message, 'warning');
            }
        };

        window.addEventListener(PAYMENT_REQUIRED_EVENT, onPaymentRequired);
        return () => window.removeEventListener(PAYMENT_REQUIRED_EVENT, onPaymentRequired);
    }, [navigate, showToast]);

    useEffect(() => {
        const flash = location.state?.flashToast;
        if (!flash?.message) return;

        showToast(flash.message, flash.type || 'success');

        const nextState = { ...(location.state || {}) };
        delete nextState.flashToast;
        navigate(`${location.pathname}${location.search}${location.hash}`, {
            replace: true,
            state: Object.keys(nextState).length > 0 ? nextState : null,
        });
    }, [
        location.hash,
        location.pathname,
        location.search,
        location.state,
        navigate,
        showToast,
    ]);

    if (!toast.show) return null;

    return (
        <Toast
            message={toast.message}
            type={toast.type}
            onClose={hideToast}
        />
    );
}
